<template>
    <div class="sip-inbox">
        <button
            type="button"
            class="sip-icon-btn sip-inbox-btn sip-header-btn--bell"
            :class="{ 'is-open': open }"
            :title="trans('communication.notifications')"
            :aria-label="trans('communication.notifications')"
            :aria-expanded="open ? 'true' : 'false'"
            @click.prevent="toggle"
        >
            <admin-icon name="bell" :size="18"></admin-icon>
            <span v-if="unreadCount > 0" class="sip-inbox-badge">{{ badgeLabel }}</span>
        </button>

        <side-drawer
            :open="open"
            size="sm"
            overlay-class="sip-inbox-drawer-overlay"
            drawer-class="sip-inbox-drawer"
            :title="trans('communication.notifications')"
            close-label="Close"
            :append-to-body="true"
            @close="close"
        >
            <template #title>
                <span class="sip-inbox-title">
                    <admin-icon name="bell" :size="18"></admin-icon>
                    {{ trans('communication.notifications') }}
                </span>
                <span v-if="unreadCount > 0" class="sip-inbox-count">{{ unreadCount }}</span>
            </template>
            <template v-if="unreadCount > 0" #actions>
                <button
                    type="button"
                    class="btn btn-sm sip-inbox-mark-all"
                    @click.prevent="markAllRead"
                >{{ trans('communication.mark_all_read') }}</button>
            </template>
            <template #footer>
                <router-link
                    class="btn btn-primary btn-sm sip-inbox-view-all"
                    to="/communication/notification"
                    @click.native="close"
                >{{ trans('communication.view_all_notifications') }}</router-link>
            </template>

            <div v-if="loading && !items.length" class="sip-inbox-loading">
                <span class="sip-skel sip-skel--lg"></span>
                <span class="sip-skel"></span>
                <span class="sip-skel sip-skel--wide"></span>
            </div>

            <empty-state
                v-else-if="!items.length"
                :title="trans('communication.inbox_empty')"
                :description="trans('communication.inbox_empty_hint')"
                icon="bell"
                size="sm"
            ></empty-state>

            <ul v-else class="sip-inbox-list" role="list">
                <li
                    v-for="item in items"
                    :key="item.id"
                    :class="['sip-inbox-item', { 'is-unread': item.unread, 'is-open': expandedId === item.id }]"
                    role="button"
                    tabindex="0"
                    @click="openItem(item)"
                    @keydown.enter.prevent="openItem(item)"
                >
                    <span class="sip-inbox-item__icon" aria-hidden="true">
                        <admin-icon :name="item.icon || 'bell'" :size="16"></admin-icon>
                    </span>
                    <span class="sip-inbox-item__body">
                        <span class="sip-inbox-item__title">{{ item.title }}</span>
                        <span v-if="item.body" class="sip-inbox-item__text">{{ item.body }}</span>
                        <span class="sip-inbox-item__time">{{ timeAgo(item.created_at) }}</span>
                    </span>
                    <span v-if="item.unread" class="sip-inbox-item__dot" aria-hidden="true"></span>
                </li>
            </ul>
        </side-drawer>
    </div>
</template>

<script>
    import { navigateInboxItem } from '@services/inbox-navigation';

    export default {
        name: 'header-notifications',
        data() {
            return {
                open: false,
                loading: false,
                items: [],
                unreadCount: 0,
                pollTimer: null,
                expandedId: null
            };
        },
        computed: {
            badgeLabel() {
                return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
            }
        },
        mounted() {
            this.fetchUnread();
            this.pollTimer = setInterval(this.fetchUnread, 60000);
            document.addEventListener('visibilitychange', this.onVisibility);
            window.addEventListener('sip-inbox-changed', this.onInboxChanged);
        },
        beforeDestroy() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
            }
            document.removeEventListener('visibilitychange', this.onVisibility);
            window.removeEventListener('sip-inbox-changed', this.onInboxChanged);
        },
        methods: {
            toggle() {
                if (this.open) {
                    this.close();
                    return;
                }
                this.openPanel();
            },
            openPanel() {
                this.open = true;
                this.fetchList();
            },
            close() {
                this.open = false;
                this.expandedId = null;
            },
            onVisibility() {
                if (document.visibilityState === 'visible') {
                    this.fetchUnread();
                }
            },
            onInboxChanged(event) {
                const count = event && event.detail ? event.detail.unread_count : null;
                if (typeof count === 'number') {
                    this.unreadCount = count;
                } else {
                    this.fetchUnread();
                }
                if (this.open) {
                    this.fetchList();
                }
            },
            timeAgo(date) {
                return helper.formatDateTimeFromNow(date);
            },
            fetchUnread() {
                axios.get('/api/inbox/unread-count')
                    .then(response => {
                        this.unreadCount = response.unread_count || 0;
                    })
                    .catch(() => {});
            },
            fetchList() {
                this.loading = true;
                axios.get('/api/inbox')
                    .then(response => {
                        this.items = response.items || [];
                        this.unreadCount = response.unread_count || 0;
                        this.loading = false;
                    })
                    .catch(() => {
                        this.loading = false;
                    });
            },
            openItem(item) {
                if (item.unread && item.source !== 'communication') {
                    this.markRead(item);
                } else if (item.unread) {
                    this.$set(item, 'unread', false);
                }
                const navigated = navigateInboxItem(this.$router, item, this.$route.path);
                if (navigated) {
                    this.close();
                    return;
                }
                this.expandedId = this.expandedId === item.id ? null : item.id;
            },
            markRead(item) {
                this.$set(item, 'unread', false);
                if (this.unreadCount > 0) {
                    this.unreadCount -= 1;
                }
                axios.post('/api/inbox/' + item.id + '/read')
                    .then(response => {
                        if (typeof response.unread_count === 'number') {
                            this.unreadCount = response.unread_count;
                        }
                    })
                    .catch(() => {});
            },
            markAllRead() {
                this.items.forEach(item => {
                    this.$set(item, 'unread', false);
                });
                this.unreadCount = 0;
                axios.post('/api/inbox/read-all').catch(() => {});
            }
        }
    }
</script>
